/**
 * Qué módulos puede abrir este usuario.
 *
 * El servidor contesta con la lista ya resuelta, o con `null` si el usuario no
 * tiene restricción de área — que es el caso de todos los usuarios de siempre.
 *
 * **`null` y `[]` NO son lo mismo**, y aquí está la trampa: `null` es «sin
 * límite, lo ve todo» y `[]` sería «no puede nada». Un `if (!modulos)` los
 * trataría igual y dejaría sin menú a la plantilla entera. Por eso esto NO
 * devuelve la lista pelada, sino la pregunta ya contestada: `puede(modulo)`.
 *
 * Esconder una entrada del menú es SÓLO cortesía: evita que alguien entre a una
 * pantalla que le daría error. El cierre de verdad es el 403 de `exigir_modulo`
 * en el backend, porque el menú no impide teclear la URL ni llamar a la API.
 */
import { useQuery } from '@tanstack/react-query'
import { authApi } from '@/services/api'

export type Modulo =
  | 'logistica' | 'muelles' | 'documentos' | 'disponibilidad'
  | 'inventario' | 'produccion' | 'compras' | 'ventas' | 'bom' | 'reports'

export function useModulos(): { puede: (m: Modulo) => boolean; cargando: boolean } {
  const { data, isLoading } = useQuery({
    queryKey: ['auth-me'],
    queryFn: authApi.getMe,
    staleTime: 60 * 60 * 1000,  // 1 h — los roles no cambian a mitad de sesión
    gcTime: 60 * 60 * 1000,
    retry: false,
  })

  // Mientras no se sabe, se asume que PUEDE: enseñar de más durante medio
  // segundo es mejor que parpadear escondiendo media aplicación. Es el mismo
  // criterio que `useSinProduccion`, y el backend no se fía de ninguno de los dos.
  const modulos = data?.modulos
  return {
    puede: (m: Modulo) => modulos == null || modulos.includes(m),
    cargando: isLoading,
  }
}
